import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faFileExport, faImages } from "@fortawesome/pro-regular-svg-icons";
import { faChevronDown, faChevronUp } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SortIcon from "@mui/icons-material/Sort";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getSpaceAvatarUrl,
  getSpaceFileDownloadUrl,
  getSpaceGuestToken,
} from "../../api";
import { PublishedSpaceResolution, ShareLinkFile } from "../../entities";
import { useHasHover } from "../../hooks/useHasHover";
import { useFileRangeSelection } from "../../hooks/useFileRangeSelection";
import { useFileSort } from "../../hooks/useFileSort";
import { useMarqueeSelection } from "../../hooks/useMarqueeSelection";
import { useSelectionKeyboard } from "../../hooks/useSelectionKeyboard";
import { useSpaceBrowser } from "../../hooks/useSpaceBrowser";
import { SpaceFolderEntry } from "../../hooks/useSpaceTree";
import { useWindowDimensions } from "../../hooks/useWindowDimensions";
import { useSkpEnvironment } from "../../providers/environment";
import { cssVar } from "../../utils/cssVar";
import { parseApiDate } from "../../utils/date";
import { basenameFromPath, formatFileSize } from "../../utils/fileFormat";
import {
  DEFAULT_FILE_SORT,
  entryComparator,
  fileSortEntry,
  FileSortKey,
  folderSortEntry,
} from "../../utils/fileSort";
import { handleGridFocusCycle } from "../../utils/focusCycle";
import {
  findSelectableItem,
  SELECTABLE_SELECTOR,
} from "../../utils/selectable";
import PreviewPage from "../../pages/preview/Preview";
import { FileCard, ItemActivation, ThumbnailSource } from "../fileCard";
import { FILE_GRID_GAP_PX, FILE_GRID_TEMPLATE_COLUMNS } from "../fileGrid";
import {
  ACTIONS_COL_W,
  FileListHeader,
  FileListRow,
  formatModified,
  LEAD_COL_W,
} from "../fileListRow";
import { FolderIcon } from "./folderIcon";
import { ReferenceFileDialog } from "./referenceFileDialog";
import { SpaceBreadcrumb } from "./spaceBreadcrumb";
import { SpaceFolderCard } from "./spaceGridCard";
import { SpaceHeader } from "./spaceHeader";
import { SpaceSelectionPill } from "./spaceSelectionPill";
import { SpaceView, SpaceViewToggle } from "./spaceViewToggle";

/** Focus key for a folder entry — a path-based DOM key for scroll/highlight,
 *  distinct from the folder's deep-link uid. */
const folderKey = (folder: SpaceFolderEntry) => `d:${folder.path.join("/")}`;

/** One display-ordered entry of the mixed folder/file listing. */
type ListingItem =
  | { kind: "folder"; folder: SpaceFolderEntry; key: string }
  | { kind: "file"; file: ShareLinkFile; key: string };

/**
 * Viewport width (px) at/below which a file row's trailing actions collapse from
 * the two inline icon buttons (preview + reference) to a single kebab, so the
 * row never crowds. Above it there is room for both.
 */
const ACTIONS_COMPACT_MAX_WIDTH = 768;

/** localStorage key persisting the user's chosen grid/list layout. */
export const SPACE_VIEW_KEY = "skp.spaceView";

/** The persisted layout (grid is the default). Safe if localStorage is blocked. */
export function readPersistedSpaceView(): SpaceView {
  try {
    const stored = window.localStorage.getItem(SPACE_VIEW_KEY);
    if (stored === "list" || stored === "grid") return stored;
  } catch {
    // localStorage unavailable — fall back to the default.
  }
  return "grid";
}

/**
 * Hover styling for the file (kebab) menu items: the whole row tints
 * primary-light and the icon + label switch to the primary color (the icon
 * follows via `color: inherit`).
 */
const FILE_MENU_ITEM_SX = {
  color: "#1A1A1A",
  "& .MuiListItemIcon-root": { color: "inherit" },
  "&:hover": {
    backgroundColor: "var(--primary-light-color)",
    color: "var(--primary-color)",
  },
} as const;

/** localStorage key persisting the user's chosen sort column + direction. */
const SPACE_SORT_KEY = "skp.spaceSort";

/** Columns offered by the toolbar sort menu (labels match the list header). */
const SORT_OPTIONS: { key: FileSortKey; label: string }[] = [
  { key: "type", label: "Type" },
  { key: "name", label: "Name" },
  { key: "size", label: "Size" },
  { key: "modified", label: "Last modified" },
];

/**
 * Wide-viewport (desktop) layout for a published space.
 *
 * Renders the space header card, a toolbar (breadcrumb + select-all, a name-sort
 * toggle and a grid/list view switch) and the current directory's folders and
 * files as either a card grid or a table-style list. Folders navigate; files
 * open the shared preview overlay and drive a floating download pill. Directory
 * state, selection, thumbnail/preview URLs, deep-linking and the download all
 * live in {@link useSpaceBrowser}.
 */
export function DesktopSpaceBrowser({
  spaceUid,
  data,
}: {
  spaceUid: string;
  data: PublishedSpaceResolution;
}) {
  const { t, i18n } = useTranslation();
  const { windowDimensions } = useWindowDimensions();
  const environment = useSkpEnvironment();
  const browser = useSpaceBrowser(spaceUid, data);
  const {
    path,
    previewFile,
    setPreviewFile,
    focusedKey,
    setFocusedKey,
    folders,
    files,
    selection,
    allUids,
  } = browser;

  // The chosen layout persists across visits (grid is the default). Seeded from
  // localStorage; `changeView` writes every toggle back.
  const [view, setView] = useState<SpaceView>(readPersistedSpaceView);
  const changeView = useCallback((next: SpaceView) => {
    setView(next);
    try {
      window.localStorage.setItem(SPACE_VIEW_KEY, next);
    } catch {
      // Ignore persistence failures; the in-memory choice still applies.
    }
  }, []);
  const hasHover = useHasHover();
  // The checkbox is revealed by hovering wherever hovering exists — including a
  // narrow desktop window. Only a device with no hover at all (touch) keeps it
  // visible the whole time, since nothing else would ever surface it.
  const alwaysShowCheckbox = !hasHover;
  // Touch follows the mobile convention: tap opens, long-press selects, and a
  // tap toggles while a selection is active. Pointers select on click and open
  // on double click.
  const itemActivation: ItemActivation = hasHover ? "select" : "preview";
  // Use the compact (mobile) page padding at/below 920px, not just at the MUI
  // `sm` breakpoint, so mid-size windows aren't over-padded.
  const compactPadding = windowDimensions.width <= 920;
  // List-view column sort — persisted across visits, since a space is a place
  // people come back to. Drives both the list and the grid; the clickable header
  // (list) and the toolbar sort menu mutate it.
  const { sort, onSort: handleSort } = useFileSort({
    storageKey: SPACE_SORT_KEY,
    initial: DEFAULT_FILE_SORT,
  });

  // One mixed listing: the type sort ranks folders ahead of every file (the
  // default starts with them); name/size/modified interleave folders and files.
  const sortedItems = useMemo<ListingItem[]>(() => {
    const items: ListingItem[] = [
      ...folders.map((folder) => ({
        kind: "folder" as const,
        folder,
        key: folderKey(folder),
      })),
      ...files.map((file) => ({ kind: "file" as const, file, key: file.uid })),
    ];
    const entries = new Map(
      items.map((it) => [
        it.key,
        it.kind === "folder"
          ? folderSortEntry(it.folder, it.key)
          : fileSortEntry(it.file),
      ])
    );
    // One comparator per sort/locale, so the collator behind it is built once
    // instead of on every comparison.
    const compare = entryComparator(sort, i18n.language);
    return items.sort((a, b) =>
      compare(entries.get(a.key)!, entries.get(b.key)!)
    );
  }, [folders, files, sort, i18n.language]);

  // Files in display order; ranges, arrow keys and preview paging use these.
  const sortedFiles = useMemo(
    () => sortedItems.flatMap((it) => (it.kind === "file" ? [it.file] : [])),
    [sortedItems]
  );
  // Below ACTIONS_COMPACT_MAX_WIDTH the row's preview + reference buttons
  // collapse to a single kebab (there's no room for both).
  const compactActions = windowDimensions.width <= ACTIONS_COMPACT_MAX_WIDTH;
  // Sort menu (the toolbar sort button) — mirrors the list header, and gives the
  // grid view a way to sort too.
  const [sortMenuAnchor, setSortMenuAnchor] = useState<HTMLElement | null>(
    null
  );
  // Kebab overflow menu + its file (Preview + Reference). Only files carry row
  // actions; folders navigate on click and have no menu.
  const [menuState, setMenuState] = useState<{
    anchor: HTMLElement;
    file: ShareLinkFile;
  } | null>(null);
  // Reference dialog. `file` is retained while `open` is false so the title
  // doesn't blank out during the close animation.
  const [refDialog, setRefDialog] = useState<{
    open: boolean;
    file: ShareLinkFile | null;
  }>({ open: false, file: null });

  const listRef = useRef<HTMLDivElement>(null);

  // Click gestures (plain = move the selection, cmd/ctrl = toggle, shift =
  // range) and the anchor they extend from. Every selection change also drops
  // the folder highlight.
  const {
    anchorRef,
    cursorRef,
    toggle: handleToggle,
    selectExclusive: handleSelectExclusive,
    selectRange: handleSelectRange,
    clear: clearSelection,
  } = useFileRangeSelection({
    files: sortedFiles,
    selection,
    onInteract: () => setFocusedKey(null),
  });

  // Keyboard multi-select (Windows-Explorer style): once a file is selected,
  // arrow keys move a cursor over the current directory's files; Shift+arrow
  // extends the selection as a contiguous range from the same anchor the click
  // gestures use. `navRef` mirrors the latest render state so the stable keydown
  // handler reads fresh values without churning its identity (keeps memoized
  // cards intact).
  const navRef = useRef<{
    sortedFiles: ShareLinkFile[];
    sortedItems: ListingItem[];
    view: SpaceView;
    selectionActive: boolean;
    selectedUids: Set<string>;
  }>({
    sortedFiles: [],
    sortedItems: [],
    view: "grid",
    selectionActive: false,
    selectedUids: selection.selectedUids,
  });

  const onListKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Tab wraps to/from the floating download pill (share-link parity).
      handleGridFocusCycle(e, {
        containerRef: listRef,
        boundarySelector: "[data-download-all]",
      });

      const nav = navRef.current;
      const files = nav.sortedFiles;
      // Arrow navigation only kicks in once selection mode is active.
      if (!nav.selectionActive || files.length === 0) return;
      if (
        e.key !== "ArrowUp" &&
        e.key !== "ArrowDown" &&
        e.key !== "ArrowLeft" &&
        e.key !== "ArrowRight"
      ) {
        return;
      }

      // Grid rows step by the live column count; the list is one column.
      const columns = (() => {
        if (nav.view !== "grid") return 1;
        const grid = listRef.current?.querySelector("[data-space-grid]");
        if (!grid) return 1;
        const tracks = window
          .getComputedStyle(grid)
          .gridTemplateColumns.split(" ")
          .filter(Boolean).length;
        return Math.max(1, tracks);
      })();

      let delta = 0;
      if (e.key === "ArrowDown") delta = nav.view === "grid" ? columns : 1;
      else if (e.key === "ArrowUp") delta = nav.view === "grid" ? -columns : -1;
      else if (e.key === "ArrowRight") delta = nav.view === "grid" ? 1 : 0;
      else if (e.key === "ArrowLeft") delta = nav.view === "grid" ? -1 : 0;
      if (delta === 0) return;
      e.preventDefault();

      // Seed the cursor from the current selection when we don't have a valid one
      // (none yet after a marquee/select-all, or one left behind in another
      // folder, since folder navigation doesn't reset it).
      if (!files.some((f) => f.uid === cursorRef.current)) {
        // The last selected file in display order, so the arrows continue from the
        // end of the selection rather than its start.
        let seed: string | undefined;
        for (const f of files) if (nav.selectedUids.has(f.uid)) seed = f.uid;
        if (!seed) return;
        cursorRef.current = seed;
        anchorRef.current = seed;
      }

      // Step through the *visible* mixed listing but only rest on files: slide
      // past folders, falling back when only folders lie toward that edge.
      const items = nav.sortedItems;
      const clamp = (i: number) => Math.max(0, Math.min(items.length - 1, i));
      const slideToFile = (from: number, step: 1 | -1): number => {
        for (let i = from; i >= 0 && i < items.length; i += step) {
          if (items[i].kind === "file") return i;
        }
        return -1;
      };
      const currentUid = cursorRef.current;
      const fromIdx = items.findIndex(
        (it) => it.kind === "file" && it.file.uid === currentUid
      );
      if (fromIdx < 0) return;
      const step: 1 | -1 = delta > 0 ? 1 : -1;
      let targetIdx = slideToFile(clamp(fromIdx + delta), step);
      if (targetIdx < 0) {
        targetIdx = slideToFile(clamp(fromIdx + delta), step === 1 ? -1 : 1);
      }
      const targetItem = targetIdx >= 0 ? items[targetIdx] : null;
      if (!targetItem || targetItem.kind !== "file") return;
      // The same two gestures a Shift+click / plain click performs, so the anchor
      // and cursor advance identically whichever the user reaches for.
      const nextUid = targetItem.file.uid;
      if (e.shiftKey) handleSelectRange(nextUid);
      else handleSelectExclusive(nextUid);

      const el = findSelectableItem(nextUid, listRef.current);
      el?.focus();
      el?.scrollIntoView({ block: "nearest" });
      // Every dependency comes from useFileRangeSelection and never changes
      // identity, so the handler still never re-registers.
    },
    [anchorRef, cursorRef, handleSelectExclusive, handleSelectRange]
  );

  // Marquee drag-select; only files carry the selectable marker. Must stay
  // referentially stable: an identity change re-registers the marquee's window
  // listeners, which must never happen mid-drag.
  const handleMarqueeSelect = useCallback(
    (uids: Set<string>) => {
      setFocusedKey(null);
      selection.setSelection(uids);
    },
    [selection.setSelection, setFocusedKey] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const marquee = useMarqueeSelection({
    containerRef: listRef,
    onSelect: handleMarqueeSelect,
    enabled: files.length > 0,
  });

  // Cmd/Ctrl+A selects the files in the *current directory* (added to any
  // cross-folder picks), not the whole space; Escape clears.
  const selectCurrentDir = useCallback(() => {
    setFocusedKey(null);
    selection.setSelection(
      new Set([...selection.selectedUids, ...files.map((f) => f.uid)])
    );
  }, [selection, files, setFocusedKey]);

  // Space-wide select-all (the breadcrumb checkbox).
  const selectAllInSpace = useCallback(() => {
    setFocusedKey(null);
    selection.selectAll();
  }, [selection.selectAll, setFocusedKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useSelectionKeyboard({
    enabled: true,
    onSelectAll: selectCurrentDir,
    onClear: clearSelection,
  });

  const modifiedAt = parseApiDate(data.modifiedAt);
  // A drive-client space always has an avatar; attempt it unconditionally and
  // let the header fall back to a glyph if it is absent / fails to load.
  const avatarUrl = getSpaceAvatarUrl(spaceUid, 184);

  // Total downloadable size of the space (files only; directory entries carry no
  // real payload) — shown in the header's download-action tooltip.
  const totalSize = useMemo(
    () =>
      data.files.reduce(
        (sum, f) =>
          f.mimeType === "inode/directory" ? sum : sum + (f.size ?? 0),
        0
      ),
    [data.files]
  );

  const navigateTo = useCallback(
    (next: string[]) => {
      browser.navigateTo(next);
      window.scrollTo({ top: 0 });
    },
    [browser]
  );

  // Single click highlights the folder (like a selected file, no checkbox)
  // and drops any file selection; double-click / Enter navigates in.
  const focusFolder = useCallback(
    (folder: SpaceFolderEntry) => {
      clearSelection();
      setFocusedKey(folderKey(folder));
    },
    [clearSelection, setFocusedKey]
  );

  const openFileMenu = useCallback(
    (anchor: HTMLElement, file: ShareLinkFile) =>
      setMenuState({ anchor, file }),
    []
  );
  const openReferenceFile = useCallback(
    (file: ShareLinkFile) => setRefDialog({ open: true, file }),
    []
  );

  // Scroll the initial deep-linked file into view once, when its card first
  // renders. Capturing the mount-time focus (a `?target=<file>` deep link) means
  // later interactive focus changes (a single click sets `focusedKey` too) never
  // scroll, so clicking around a listing stays put.
  const deepLinkKeyRef = useRef(focusedKey);
  const didDeepLinkScrollRef = useRef(false);
  useEffect(() => {
    const key = deepLinkKeyRef.current;
    if (!key || didDeepLinkScrollRef.current) return;
    const el = findSelectableItem(key);
    if (!el) return;
    didDeepLinkScrollRef.current = true;
    el.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [files, folders]);

  // One shared, stable thumbnail source for every card (keeps FileCard's
  // memoization intact). Thumbnails authenticate via the guest cookie.
  const thumbnails = useMemo<ThumbnailSource>(
    () => ({ buildUrl: browser.buildThumbnailUrl }),
    [browser.buildThumbnailUrl]
  );

  const isEmpty = folders.length === 0 && files.length === 0;
  const selectionActive = selection.selectedCount > 0;

  // Preview paging over the files in display order; `setPreviewFile` keeps
  // the URL target on the shown file, so it stays shareable.
  const previewIndex = previewFile
    ? sortedFiles.findIndex((f) => f.uid === previewFile.uid)
    : -1;
  const navigatePreview = useCallback(
    (direction: 1 | -1) => {
      if (!previewFile) return;
      const idx = sortedFiles.findIndex((f) => f.uid === previewFile.uid);
      const next = sortedFiles[idx + direction];
      if (next) setPreviewFile(next);
    },
    [previewFile, sortedFiles, setPreviewFile]
  );

  // Closing the preview scrolls the last-viewed file back into view.
  const closePreview = useCallback(() => {
    const uid = previewFile?.uid;
    setPreviewFile(null);
    if (!uid) return;
    const el = findSelectableItem(uid, listRef.current);
    el?.scrollIntoView({ block: "nearest" });
  }, [previewFile, setPreviewFile]);

  // Mirror the latest render state for the stable keydown handler to read.
  navRef.current = {
    sortedFiles,
    sortedItems,
    view,
    selectionActive,
    selectedUids: selection.selectedUids,
  };

  return (
    <Box
      // Clicking any empty space (header, toolbar, gaps) clears the selection;
      // clicks on selectable items, buttons and links are ignored by the handler.
      // The marquee-drag's trailing click is swallowed here in the capture phase
      // (before the background-clear runs) no matter where the drag was released
      // — including in the side gutters outside the listing. The whole viewer is
      // non-selectable so native text selection / image dragging never fights
      // the file selection.
      onClickCapture={marquee.handleClickCapture}
      onClick={(e) => {
        selection.handleBackgroundClick(e);
        // The folder highlight follows the same click-away rule.
        if (
          !(e.target as HTMLElement).closest(
            `${SELECTABLE_SELECTOR}, button, a`
          )
        ) {
          setFocusedKey(null);
        }
      }}
      // Full-bleed with generous side padding — mirrors the share-link viewer
      // (`px-5 sm:px-[110px]`) rather than a fixed max-width column, so the
      // `auto-fill` grid/list fills the viewport and gains columns as it widens.
      sx={{
        px: compactPadding ? "20px" : "110px",
        pt: compactPadding ? 3 : 5,
        pb: 14, // room for the floating selection pill
        userSelect: "none",
      }}
    >
      <SpaceHeader
        spaceName={data.spaceName}
        provider={data.provider ?? environment?.customerName}
        fileCount={data.fileCount}
        totalSize={totalSize}
        modifiedAt={modifiedAt ?? undefined}
        avatarUrl={avatarUrl}
        blurhash={data.blurhash}
        onDownloadAll={browser.downloadAll}
      />

      {/* ---- Toolbar: breadcrumb + select-all | view toggle ---- */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 3, mb: 2 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <SpaceBreadcrumb
            spaceName={data.spaceName}
            path={path}
            onNavigate={navigateTo}
            selection={{
              selectedCount: selection.selectedCount,
              totalCount: allUids.length,
              onSelectAll: selectAllInSpace,
              onClear: clearSelection,
            }}
          />
        </Box>
        {/* Sort button sits 23px to the left of the view toggle. */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "23px" }}>
          <Tooltip title={t("Sort")} placement="top">
            <IconButton
              aria-label={t("Sort")}
              onClick={(e) => setSortMenuAnchor(e.currentTarget)}
              sx={{
                width: 34,
                height: 34,
                flex: "none",
                color: sortMenuAnchor ? "var(--primary-color)" : "#1A1A1A",
                "&:hover": { backgroundColor: "rgba(17,17,26,0.05)" },
              }}
            >
              <SortIcon sx={{ fontSize: 22 }} />
            </IconButton>
          </Tooltip>
          <SpaceViewToggle view={view} onChange={changeView} />
        </Box>
      </Box>

      {/* ---- Listing ---- */}
      {isEmpty ? (
        <Box
          sx={{ pt: 8, textAlign: "center", fontSize: 14, color: "#9a9aa4" }}
        >
          {t("This folder is empty.")}
        </Box>
      ) : (
        <Box
          ref={listRef}
          className="select-none"
          onKeyDown={onListKeyDown}
          onMouseDown={marquee.handleMouseDown}
          // Marquee start zone: negative margins + equal padding let a drag
          // start in the whitespace around the listing without moving it.
          sx={{
            mt: "-16px",
            pt: "16px",
            mx: compactPadding ? "-12px" : "-60px",
            px: compactPadding ? "12px" : "60px",
            mb: "-48px",
            pb: "48px",
          }}
        >
          {view === "grid" ? (
            <Box
              data-space-grid
              sx={{
                display: "grid",
                gridTemplateColumns: FILE_GRID_TEMPLATE_COLUMNS,
                gap: `${FILE_GRID_GAP_PX}px`,
              }}
            >
              {sortedItems.map((item) =>
                item.kind === "folder" ? (
                  <SpaceFolderCard
                    key={item.key}
                    folder={item.folder}
                    onOpen={navigateTo}
                    onFocusItem={() => focusFolder(item.folder)}
                    openOnTap={!hasHover}
                    focused={focusedKey === item.key}
                  />
                ) : (
                  <FileCard
                    key={item.key}
                    file={item.file}
                    isSelected={selection.isSelected(item.file.uid)}
                    onToggleSelect={handleToggle}
                    onSelectExclusive={handleSelectExclusive}
                    onSelectRange={handleSelectRange}
                    onOpenPreview={setPreviewFile}
                    thumbnails={thumbnails}
                    checkboxPlacement="overlay"
                    checkboxAlwaysVisible={alwaysShowCheckbox}
                    activation={itemActivation}
                    selectionActive={selectionActive}
                    selecting={marquee.isDragging}
                    focused={focusedKey === item.file.uid}
                    onMoreActions={openFileMenu}
                  />
                )
              )}
            </Box>
          ) : (
            <>
              <FileListHeader
                sort={sort}
                onSort={handleSort}
                hasActions
                sticky
              />
              {/* `pt` keeps the first row's focus outline clear of the sticky
                  header's divider (which would otherwise clip its top edge). */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0,
                  pt: "3px",
                  pb: 3,
                }}
              >
                {sortedItems.map((item, i) => {
                  if (item.kind === "folder") {
                    return (
                      <SpaceFolderListRow
                        key={item.key}
                        folder={item.folder}
                        onOpen={navigateTo}
                        onFocusItem={() => focusFolder(item.folder)}
                        openOnTap={!hasHover}
                        focused={focusedKey === item.key}
                      />
                    );
                  }
                  // A folder between selected rows splits the merged block.
                  const above = sortedItems[i - 1];
                  const below = sortedItems[i + 1];
                  return (
                    <FileListRow
                      key={item.key}
                      file={item.file}
                      isSelected={selection.isSelected(item.file.uid)}
                      selectedAbove={
                        above?.kind === "file" &&
                        selection.isSelected(above.file.uid)
                      }
                      selectedBelow={
                        below?.kind === "file" &&
                        selection.isSelected(below.file.uid)
                      }
                      onToggleSelect={handleToggle}
                      onSelectExclusive={handleSelectExclusive}
                      onSelectRange={handleSelectRange}
                      onOpenPreview={setPreviewFile}
                      selectionActive={selectionActive}
                      activation={itemActivation}
                      selecting={marquee.isDragging}
                      focused={focusedKey === item.file.uid}
                      checkboxAlwaysVisible={alwaysShowCheckbox}
                      onReference={openReferenceFile}
                      onMoreActions={openFileMenu}
                      compactActions={compactActions}
                      menuOpen={menuState?.file.uid === item.file.uid}
                    />
                  );
                })}
              </Box>
            </>
          )}
        </Box>
      )}

      {/* Marquee rubber-band box (fixed; positioned by the hook via boxRef). */}
      {marquee.isDragging && (
        <div
          ref={marquee.boxRef}
          className="pointer-events-none fixed left-0 top-0 z-30 rounded-[2px]"
          style={{
            border: `1px solid ${cssVar("--primary-color")}`,
            backgroundColor:
              "color-mix(in srgb, var(--primary-color) 18%, transparent)",
            willChange: "transform, width, height",
          }}
        />
      )}

      <SpaceSelectionPill
        count={selection.selectedCount}
        onDownload={browser.downloadSelected}
      />

      {/* Sort menu (toolbar sort button). Selecting a column sorts by it; the
          active column shows its direction and re-selecting flips it. */}
      <Menu
        anchorEl={sortMenuAnchor}
        open={!!sortMenuAnchor}
        onClose={() => setSortMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {SORT_OPTIONS.map(({ key, label }) => {
          const active = sort.key === key;
          return (
            <MenuItem
              key={key}
              selected={active}
              onClick={() => handleSort(key)}
              sx={{
                minWidth: 180,
                fontSize: 14,
                "&:hover": { backgroundColor: "var(--primary-light-color)" },
              }}
            >
              <ListItemText primaryTypographyProps={{ fontSize: 14 }}>
                {t(label)}
              </ListItemText>
              {active && (
                <FontAwesomeIcon
                  // Ascending points up, like the list header's chevron.
                  icon={
                    (sort.direction === "asc"
                      ? faChevronUp
                      : faChevronDown) as IconProp
                  }
                  style={{ fontSize: 10, marginLeft: 16, color: "#1A1A1A" }}
                />
              )}
            </MenuItem>
          );
        })}
      </Menu>

      {/* Kebab overflow menu for a file — Preview + Reference. */}
      <Menu
        anchorEl={menuState?.anchor ?? null}
        open={!!menuState}
        onClose={() => setMenuState(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            const f = menuState?.file ?? null;
            setMenuState(null);
            if (f) setPreviewFile(f);
          }}
          sx={FILE_MENU_ITEM_SX}
        >
          <ListItemIcon>
            <FontAwesomeIcon
              icon={faImages as IconProp}
              style={{ fontSize: 15 }}
            />
          </ListItemIcon>
          <ListItemText
            primaryTypographyProps={{ fontSize: 14, color: "inherit" }}
          >
            {t("Preview")}
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            const f = menuState?.file ?? null;
            setMenuState(null);
            if (f) setRefDialog({ open: true, file: f });
          }}
          sx={FILE_MENU_ITEM_SX}
        >
          <ListItemIcon>
            <FontAwesomeIcon
              icon={faFileExport as IconProp}
              style={{ fontSize: 15 }}
            />
          </ListItemIcon>
          <ListItemText
            primaryTypographyProps={{ fontSize: 14, color: "inherit" }}
          >
            {t("Reference this file")}
          </ListItemText>
        </MenuItem>
      </Menu>

      <ReferenceFileDialog
        open={refDialog.open}
        file={refDialog.file}
        spaceUid={spaceUid}
        onClose={() => setRefDialog((d) => ({ ...d, open: false }))}
      />

      {/* No `key`: paging swaps the per-file props on the same mounted
          overlay, so the outgoing file can stay while the next one loads. */}
      {previewFile && (
        <PreviewPage
          transferRecipientId={spaceUid}
          objectUid={previewFile.uid}
          fileName={basenameFromPath(previewFile.path) || previewFile.uid}
          mimeType={previewFile.mimeType}
          aspectRatio={previewFile.aspectRatio}
          filePreviewUrl={new URL(browser.buildPreviewUrl(previewFile))}
          fileDownloadUrl={getSpaceFileDownloadUrl(spaceUid, previewFile.uid)}
          refreshAuth={browser.previewRefreshAuth}
          getAuthToken={getSpaceGuestToken}
          navigation={
            previewIndex >= 0 && sortedFiles.length > 1
              ? {
                  hasPrevious: previewIndex > 0,
                  hasNext: previewIndex < sortedFiles.length - 1,
                  onNavigate: navigatePreview,
                }
              : undefined
          }
          onClose={closePreview}
        />
      )}
    </Box>
  );
}

/**
 * List-view row for a subfolder, mirroring {@link FileListRow}'s columns so
 * folders and files line up under the shared header. Single click highlights
 * (like a selected file, no checkbox), double click / Enter navigates in; on
 * touch a single tap navigates directly. Folders aren't selectable, so
 * modified clicks do nothing.
 */
function SpaceFolderListRow({
  folder,
  onOpen,
  onFocusItem,
  openOnTap,
  focused,
}: {
  folder: SpaceFolderEntry;
  /** Navigate into the folder (double-click / Enter). */
  onOpen: (path: string[]) => void;
  /** Highlight the row without navigating (single click). */
  onFocusItem: () => void;
  /** Touch: a single tap navigates (there is no highlight step). */
  openOnTap: boolean;
  /** Controlled highlight, same fill as a selected file row. */
  focused: boolean;
}) {
  const { i18n } = useTranslation();
  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={folder.name}
      onClick={(e) => {
        // Handled here, not by the listing's background-clear handler; the
        // file multi-select gestures do nothing on a folder.
        e.stopPropagation();
        if (e.metaKey || e.ctrlKey || e.shiftKey) return;
        if (openOnTap) onOpen(folder.path);
        else onFocusItem();
      }}
      onDoubleClick={() => onOpen(folder.path)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          onOpen(folder.path);
        } else if (e.key === " ") {
          e.preventDefault();
          onFocusItem();
        }
      }}
      className="file-card file-list-row"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 1.5,
        height: 52,
        borderRadius: "14px",
        cursor: "pointer",
        userSelect: "none",
        outline: "none",
        // Single-clicked folders use the selected row's fill; the keyboard
        // tint comes from CSS (index.css).
        backgroundColor: focused ? cssVar("--primary-light-color") : undefined,
        transition: "background-color 0.12s ease",
      }}
    >
      {/* Leading spacer aligns with the file rows' checkbox column (folders
          aren't selectable). */}
      <Box sx={{ width: LEAD_COL_W, flexShrink: 0 }} aria-hidden />
      <Box
        sx={{
          width: 33,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <FolderIcon size={33} />
      </Box>
      <Box
        sx={{
          minWidth: 0,
          flex: "1 1 0",
          fontSize: 15,
          fontWeight: 400,
          color: "#1a1a1a",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={folder.name}
      >
        {folder.name}
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          fontSize: 15,
          lineHeight: "22px",
          color: "#1A1A1A",
        }}
      >
        <Box
          className="file-list-col-size"
          sx={{ width: 96, textAlign: "left" }}
        >
          {formatFileSize(folder.size, i18n.language)}
        </Box>
        <Box
          className="file-list-col-modified"
          sx={{ width: 112, textAlign: "left" }}
        >
          {formatModified(folder.modifiedAt, i18n.language)}
        </Box>
      </Box>
      {/* Empty spacer matching the file rows' actions column, so the folder's
          size/modified columns stay aligned under the shared header even though
          folders carry no row actions. */}
      <Box sx={{ width: ACTIONS_COL_W, flexShrink: 0 }} aria-hidden />
    </Box>
  );
}
